// ── Session report / viewer ("/sessions/:session_id/report") ───────────────
// The session-context report viewer uses the real HTML attachment download,
// role-scoped review controls, and SA-only regeneration. Content remains escaped
// and participant access remains fail-closed.
// id is a STRING — passed VERBATIM, never Number().

import { Badge, DetailSurface, Module, ModuleBody, ModuleHead } from "@vjal/ui";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { canRoleReadReport } from "../../domain/sensitive-surfaces";
import { reportTypeLabels } from "../../domain/report-taxonomy";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingArtifactDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  canAccessReportsModule,
  isReportReady,
  isValidSessionId,
  orDash,
  reportAccessFields,
  reportStatusLabel,
  reportStatusTone
} from "./reports-helpers";
import { ReportContentSection } from "./report-detail/report-content-section";
import { ReportReviewSection } from "./report-detail/report-review-section";
import { ReportDownloadSection } from "./report-detail/report-download-section";
import { ReportRegenerateSection } from "./report-detail/report-regenerate-section";
import { ReportGenerateSection } from "./report-detail/report-generate-section";

const services = getCoachingServices();

export function SessionReportPage({ role, routePath }: ProductScreenContext) {
  const sessionId = liveRecordIdFromPath(routePath, "sessions");
  const validId = isValidSessionId(sessionId);
  const moduleDenied = !canAccessReportsModule(role);

  const query = useHonestQuery<CoachingArtifactDto>(
    [...coachingQueryKeys.sessionDetail(sessionId ?? ""), "session-report"],
    () => services.reports.sessionReport(sessionId as string),
    { enabled: validId && !moduleDenied }
  );
  const report = query.data ?? null;

  if (moduleDenied) {
    return (
      <section aria-label="Session report" className="col-span-12" data-testid="session-report-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to this report"
            message="The reports area is for coaching staff and assigned leads."
          />
        </div>
      </section>
    );
  }

  // D-3 belt-and-suspenders on the single-report read: if a backend regression
  // returned a non-readable audience tier, drop it to generic-not-found.
  const readable = report != null && canRoleReadReport(role, reportAccessFields(report));

  if (!validId || query.posture !== null || !report || !readable) {
    const posture = !validId ? "empty" : query.posture ?? (report && !readable ? "empty" : "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Session report" className="col-span-12 flex flex-col gap-4" data-testid="session-report-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Report not found" : undefined}
            message={
              notFound
                ? "No report exists for this session yet, or it isn't one you're authorized to see."
                : undefined
            }
          />
        </div>
        {/* When no report exists yet, SA/Admin/coach-in-scope may still ENQUEUE
            generation — the response is honest pending/queued, never fabricated. */}
        {notFound && validId ? (
          <div className="grid grid-cols-12 gap-4">
            <ReportGenerateSection role={role} sessionId={sessionId} />
          </div>
        ) : null}
      </section>
    );
  }

  const access = reportAccessFields(report);
  const ready = isReportReady(report.status);
  const row = report as CoachingArtifactDto & Record<string, unknown>;

  return (
    <section aria-label="Session report" className="col-span-12 flex flex-col gap-4" data-testid="session-report-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 session-report. */}
      <DetailSurface
        title={
          <span className="flex flex-wrap items-center gap-3">
            {orDash(report.session_title ?? (row.sessionTitle as string | undefined) ?? report.title)}
            <span data-report-type={access.reportType ?? "team_admin"}>
              <Badge variant="info">
                {access.reportType ? reportTypeLabels[access.reportType] : "Team / company"}
              </Badge>
            </span>
            <Badge variant={reportStatusTone(report.status) === "danger" ? "danger" : reportStatusTone(report.status)}>
              {reportStatusLabel(report.status)}
            </Badge>
          </span>
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/reports" className="text-link no-underline hover:underline">
                ← All reports
              </Link>
            ),
          },
          {
            id: "cohort",
            label: "Cohort",
            value: orDash(report.cohort_name ?? (row.cohortName as string | undefined)),
          },
          {
            id: "session",
            label: "Session",
            value: orDash(report.session_title ?? (row.sessionTitle as string | undefined)),
          },
        ]}
        titleExactName="Report title">
        <div className="mt-4 grid grid-cols-12 gap-4">
          {ready ? (
            <ReportContentSection report={report} />
          ) : (
            <Module span={8}>
              <ModuleHead title="Report" subtitle="This report isn't ready to read yet." />
              <ModuleBody>
                <HonestState
                  posture={reportStatusTone(report.status) === "danger" ? "error" : "unavailable"}
                  title={reportStatusTone(report.status) === "danger" ? "Report generation failed" : "Report not ready yet"}
                  message={
                    reportStatusTone(report.status) === "danger"
                      ? "This report didn't generate successfully. No content is available."
                      : "This report is still being generated. Its content will appear here once it's ready."
                  }
                />
              </ModuleBody>
            </Module>
          )}
          <ReportReviewSection report={report} role={role} />
          <ReportDownloadSection report={report} role={role} />
          <ReportGenerateSection role={role} sessionId={sessionId} />
          <ReportRegenerateSection role={role} sessionId={report.session_id ?? sessionId} />
        </div>
      </DetailSurface>
    </section>
  );
}
